import { useState } from "preact/hooks";

import {
  buildCompanyRoleStats,
  filterAndSortCompanies,
  normalizeCompanyDomain,
  type CompanyDomainFilter,
  type CompanySort,
  type CompanyStatusFilter,
} from "../lib/companyList";
import type { Ats, Company } from "../lib/types";
import { allJobs } from "../store/appStore";

const ATS_OPTIONS: Ats[] = [
  "greenhouse", "lever", "ashby", "workable", "custom", "unsupported", "amazon", "icims", "phenom", "gem",
];

const STATUS_OPTIONS: { value: CompanyStatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "needs_ats", label: "Needs ATS" },
  { value: "skipped", label: "Skipped" },
];

const DOMAIN_OPTIONS: { value: CompanyDomainFilter; label: string }[] = [
  { value: "all", label: "All domains" },
  { value: "has", label: "Has domain" },
  { value: "missing", label: "Missing domain" },
];

const SORT_OPTIONS: { value: CompanySort; label: string }[] = [
  { value: "name", label: "Name A–Z" },
  { value: "roles", label: "Open roles" },
  { value: "recent", label: "Recently surfaced" },
  { value: "needs_ats", label: "Needs ATS first" },
];

function statusBadge(company: Company) {
  if (company.inactive) {
    return <span class="company-status-badge company-status-inactive">Inactive</span>;
  }
  if (company.ats !== "unsupported") return null;
  if (company.resolution_status === "pending") {
    return <span class="company-status-badge company-status-pending">Needs ATS</span>;
  }
  return <span class="company-status-badge company-status-skipped">Skipped</span>;
}

function applyAtsChange(company: Company, ats: Ats | string): Company {
  if (ats === "unsupported") {
    const next: Company = {
      ...company,
      ats,
      resolution_status: "skipped",
      skip_reason: "manual",
    };
    delete next.resolution_reason;
    delete next.original_query;
    delete next.unresolved_at;
    return next;
  }

  const next: Company = { ...company, ats };
  delete next.resolution_status;
  delete next.resolution_reason;
  delete next.original_query;
  delete next.unresolved_at;
  delete next.skip_reason;
  return next;
}

interface Props {
  companies: Company[];
  tier1Companies: string[];
  onChange: (companies: Company[]) => void;
  onTier1Change: (tier1Companies: string[]) => void;
}

function isTier1(name: string, tier1Companies: string[]): boolean {
  const lower = name.trim().toLowerCase();
  if (!lower) return false;
  return tier1Companies.some((c) => c.toLowerCase() === lower);
}

/** Structured Companies table — editable name/slug/domain, ATS, filters, Pause/Resume. */
export function CompaniesEditor({ companies, tier1Companies, onChange, onTier1Change }: Props) {
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [newAts, setNewAts] = useState<Ats>("greenhouse");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<CompanyStatusFilter>("active");
  const [atsFilter, setAtsFilter] = useState<string>("all");
  const [domainFilter, setDomainFilter] = useState<CompanyDomainFilter>("all");
  const [sort, setSort] = useState<CompanySort>("name");
  /** Expanded company-card indexes; all cards start collapsed. */
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  // Read signal in render so role counts stay in sync with the jobs store.
  const roleStats = buildCompanyRoleStats(allJobs.value);
  const rows = filterAndSortCompanies(companies, {
    search,
    status: statusFilter,
    ats: atsFilter,
    domain: domainFilter,
    sort,
    roleStats,
  });

  function update(idx: number, patch: Partial<Company>) {
    const prev = companies[idx];
    const next = companies.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    onChange(next);
    // Keep tier-1 list in sync when a starred company is renamed.
    if (patch.name !== undefined && prev && prev.name !== patch.name) {
      const oldLower = prev.name.trim().toLowerCase();
      if (oldLower && isTier1(prev.name, tier1Companies)) {
        const renamed = patch.name.trim();
        onTier1Change(
          tier1Companies
            .map((c) => (c.toLowerCase() === oldLower ? renamed : c))
            .filter(Boolean),
        );
      }
    }
  }

  function setDomain(idx: number, raw: string) {
    const cleaned = normalizeCompanyDomain(raw);
    update(idx, { domain: cleaned || undefined });
  }

  function pause(idx: number) {
    const next = companies.map((c, i) => (i === idx ? { ...c, inactive: true } : c));
    onChange(next);
  }

  function resume(idx: number) {
    const next = companies.map((c, i) => {
      if (i !== idx) return c;
      const restored = { ...c };
      delete restored.inactive;
      return restored;
    });
    onChange(next);
  }

  function addRow() {
    if (!newName.trim() || !newSlug.trim()) return;
    const company: Company = {
      name: newName.trim(),
      slug: newSlug.trim(),
      ats: newAts,
    };
    const domain = normalizeCompanyDomain(newDomain);
    if (domain) company.domain = domain;
    if (newAts === "unsupported") {
      company.resolution_status = "skipped";
      company.skip_reason = "manual";
    }
    onChange([...companies, company]);
    setNewName("");
    setNewSlug("");
    setNewDomain("");
    setNewAts("greenhouse");
  }

  function pauseResumeButton(c: Company, idx: number) {
    return c.inactive ? (
      <button
        class="row-resume"
        onClick={() => resume(idx)}
        aria-label={`Resume ${c.name}`}
        title="Resume scanning this company"
      >
        Resume
      </button>
    ) : (
      <button
        class="row-pause"
        onClick={() => pause(idx)}
        aria-label={`Pause ${c.name}`}
        title="Pause scanning (keeps company for later)"
      >
        Pause
      </button>
    );
  }

  function toggleTier1(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    const lower = trimmed.toLowerCase();
    if (isTier1(trimmed, tier1Companies)) {
      onTier1Change(tier1Companies.filter((c) => c.toLowerCase() !== lower));
    } else {
      onTier1Change([...tier1Companies, trimmed]);
    }
  }

  function starButton(c: Company, opts?: { stopPropagation?: boolean }) {
    const starred = isTier1(c.name, tier1Companies);
    return (
      <button
        type="button"
        class={`company-star-btn${starred ? " is-starred" : ""}`}
        onClick={(e) => {
          if (opts?.stopPropagation) e.stopPropagation();
          toggleTier1(c.name);
        }}
        aria-label={starred ? `Remove ${c.name} from score boost` : `Star ${c.name} for score boost`}
        aria-pressed={starred}
        title={starred ? "Remove +30 score boost" : "Star for +30 relevance score boost"}
        disabled={!c.name.trim()}
      >
        {starred ? "★" : "☆"}
      </button>
    );
  }

  return (
    <div class="companies-editor">
      <div class="companies-toolbar">
        <input
          class="form-control companies-search"
          type="search"
          placeholder="Search name, slug, domain…"
          value={search}
          onInput={(e) => setSearch(e.currentTarget.value)}
          aria-label="Search companies"
        />
        <div class="companies-toolbar-filters">
          <select
            class="form-control"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.currentTarget.value as CompanyStatusFilter)}
            aria-label="Filter by status"
          >
            {STATUS_OPTIONS.map((o) => (
              <option value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            class="form-control"
            value={atsFilter}
            onChange={(e) => setAtsFilter(e.currentTarget.value)}
            aria-label="Filter by ATS"
          >
            <option value="all">All ATS</option>
            {ATS_OPTIONS.map((a) => (
              <option value={a}>{a}</option>
            ))}
          </select>
          <select
            class="form-control"
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.currentTarget.value as CompanyDomainFilter)}
            aria-label="Filter by domain"
          >
            {DOMAIN_OPTIONS.map((o) => (
              <option value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            class="form-control"
            value={sort}
            onChange={(e) => setSort(e.currentTarget.value as CompanySort)}
            aria-label="Sort companies"
          >
            {SORT_OPTIONS.map((o) => (
              <option value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <span class="companies-showing" aria-live="polite">
          Showing {rows.length} of {companies.length}
        </span>
      </div>

      <div class="companies-table-wrap">
        <table class="companies-table">
          <thead>
            <tr>
              <th class="company-star-col" aria-label="Score boost" />
              <th>Name</th>
              <th>Slug</th>
              <th>Domain</th>
              <th>ATS</th>
              <th>Roles</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} class="companies-empty">
                  No companies match these filters.
                </td>
              </tr>
            ) : (
              rows.map(({ company: c, index: idx }) => {
                const stats = roleStats.get(c.name.toLowerCase());
                return (
                  <tr key={`${c.slug}-${idx}`} class={c.inactive ? "company-row-inactive" : undefined}>
                    <td class="company-star-col">{starButton(c)}</td>
                    <td>
                      <input
                        class="form-control"
                        value={c.name}
                        onInput={(e) => update(idx, { name: e.currentTarget.value })}
                        aria-label={`Company ${idx + 1} name`}
                      />
                    </td>
                    <td>
                      <input
                        class="form-control"
                        value={c.slug}
                        onInput={(e) => update(idx, { slug: e.currentTarget.value })}
                        aria-label={`Company ${idx + 1} slug`}
                      />
                    </td>
                    <td>
                      <input
                        class="form-control"
                        value={c.domain || ""}
                        placeholder="apple.com"
                        onInput={(e) => update(idx, { domain: e.currentTarget.value || undefined })}
                        onBlur={(e) => setDomain(idx, e.currentTarget.value)}
                        aria-label={`Company ${idx + 1} domain`}
                      />
                    </td>
                    <td>
                      <select
                        class="form-control"
                        value={c.ats}
                        onChange={(e) => update(idx, applyAtsChange(c, e.currentTarget.value as Ats))}
                        aria-label={`Company ${idx + 1} ATS`}
                      >
                        {ATS_OPTIONS.map((a) => (
                          <option value={a}>{a}</option>
                        ))}
                      </select>
                    </td>
                    <td class="company-roles-cell">{stats?.roleCount ?? 0}</td>
                    <td>{statusBadge(c)}</td>
                    <td class="company-row-actions">{pauseResumeButton(c, idx)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div class="companies-cards" aria-label="Companies list">
        {rows.length === 0 ? (
          <p class="companies-empty">No companies match these filters.</p>
        ) : (
          rows.map(({ company: c, index: idx }) => {
            const stats = roleStats.get(c.name.toLowerCase());
            const isOpen = !!expanded[idx];
            const panelId = `company-card-panel-${idx}`;
            return (
              <article
                key={`card-${c.slug}-${idx}`}
                class={`company-card${isOpen ? " is-expanded" : " is-collapsed"}${c.inactive ? " company-row-inactive" : ""}${isTier1(c.name, tier1Companies) ? " is-starred" : ""}`}
              >
                <div class="company-card-summary-row">
                  {starButton(c, { stopPropagation: true })}
                  <button
                    type="button"
                    class="company-card-summary"
                    onClick={() => setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }))}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                  >
                    <span class="company-card-summary-main">
                      <span class="company-card-name">{c.name || "Untitled company"}</span>
                      <span class="company-card-summary-meta">
                        <span class="company-card-ats">{c.ats}</span>
                        {c.domain ? <span class="company-card-domain">{c.domain}</span> : null}
                        <span class="company-card-roles">{stats?.roleCount ?? 0} roles</span>
                        {statusBadge(c)}
                      </span>
                    </span>
                    <span class="company-card-chevron" aria-hidden="true">{isOpen ? "▾" : "▸"}</span>
                  </button>
                </div>
                {isOpen && (
                  <div class="company-card-body" id={panelId}>
                    <div class="company-card-top">
                      <div class="company-card-meta">
                        <span class="company-card-roles">{stats?.roleCount ?? 0} roles</span>
                        {statusBadge(c)}
                      </div>
                      <div class="company-card-actions">
                        {starButton(c)}
                        {pauseResumeButton(c, idx)}
                      </div>
                    </div>
                    <div class="company-card-fields">
                      <label class="company-card-field">
                        <span>Name</span>
                        <input
                          class="form-control"
                          value={c.name}
                          onInput={(e) => update(idx, { name: e.currentTarget.value })}
                          aria-label={`Company ${idx + 1} name`}
                        />
                      </label>
                      <label class="company-card-field">
                        <span>Slug</span>
                        <input
                          class="form-control"
                          value={c.slug}
                          onInput={(e) => update(idx, { slug: e.currentTarget.value })}
                          aria-label={`Company ${idx + 1} slug`}
                        />
                      </label>
                      <label class="company-card-field">
                        <span>Domain</span>
                        <input
                          class="form-control"
                          value={c.domain || ""}
                          placeholder="apple.com"
                          onInput={(e) => update(idx, { domain: e.currentTarget.value || undefined })}
                          onBlur={(e) => setDomain(idx, e.currentTarget.value)}
                          aria-label={`Company ${idx + 1} domain`}
                        />
                      </label>
                      <label class="company-card-field">
                        <span>ATS</span>
                        <select
                          class="form-control"
                          value={c.ats}
                          onChange={(e) => update(idx, applyAtsChange(c, e.currentTarget.value as Ats))}
                          aria-label={`Company ${idx + 1} ATS`}
                        >
                          {ATS_OPTIONS.map((a) => (
                            <option value={a}>{a}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>

      <div class="companies-add-row">
        <input
          class="form-control"
          placeholder="Name"
          value={newName}
          onInput={(e) => setNewName(e.currentTarget.value)}
          aria-label="New company name"
        />
        <input
          class="form-control"
          placeholder="Slug"
          value={newSlug}
          onInput={(e) => setNewSlug(e.currentTarget.value)}
          aria-label="New company slug"
        />
        <input
          class="form-control"
          placeholder="Domain (optional)"
          value={newDomain}
          onInput={(e) => setNewDomain(e.currentTarget.value)}
          onBlur={(e) => setNewDomain(normalizeCompanyDomain(e.currentTarget.value))}
          aria-label="New company domain"
        />
        <select
          class="form-control"
          value={newAts}
          onChange={(e) => setNewAts(e.currentTarget.value as Ats)}
          aria-label="New company ATS"
        >
          {ATS_OPTIONS.map((a) => (
            <option value={a}>{a}</option>
          ))}
        </select>
        <button class="btn-primary" onClick={addRow} disabled={!newName.trim() || !newSlug.trim()}>
          Add row
        </button>
      </div>

      <p class="form-hint companies-hint">
        Set <strong>Domain</strong> for logos. <strong>Star</strong> adds a +30 relevance
        score boost. <strong>Pause</strong> skips scans without deleting.
      </p>
    </div>
  );
}
